import { useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { alphaVantageService } from '@/services/alphaVantage/alphaVantageService';
import { generateMockCorrelation, normalizeSymbol } from './utils';
import { MarketDataMap } from './types';

export const useCorrelation = (
  marketData: MarketDataMap,
  setMarketData: React.Dispatch<React.SetStateAction<MarketDataMap>>,
  apiCallCount: number,
  setApiCallCount: (cb: (prev: number) => number) => void,
  isDevMode: boolean
) => {
  const { toast } = useToast();

  // Get correlation between two symbols
  const getCorrelation = useCallback(async (symbolA: string, symbolB: string): Promise<number> => {
    const symbolAUpper = normalizeSymbol(symbolA);
    const symbolBUpper = normalizeSymbol(symbolB);
    
    // Check if correlation already exists in cache
    if (
      marketData[symbolAUpper]?.correlations[symbolBUpper] !== undefined ||
      marketData[symbolBUpper]?.correlations[symbolAUpper] !== undefined
    ) {
      return marketData[symbolAUpper]?.correlations[symbolBUpper] || 
             marketData[symbolBUpper]?.correlations[symbolAUpper] || 
             0;
    }
    
    try {
      // In development or when API limit reached, generate mock correlation
      if (isDevMode || apiCallCount >= 5) {
        const correlation = generateMockCorrelation();
        
        // Update the cache
        setMarketData(prev => {
          const updated = { ...prev };
          
          if (!updated[symbolAUpper]) {
            updated[symbolAUpper] = { lastPrice: 0, zscore: 0, volatility: 0, correlations: {} };
          }
          if (!updated[symbolBUpper]) {
            updated[symbolBUpper] = { lastPrice: 0, zscore: 0, volatility: 0, correlations: {} };
          }
          
          updated[symbolAUpper].correlations[symbolBUpper] = correlation;
          updated[symbolBUpper].correlations[symbolAUpper] = correlation;
          
          return updated;
        });
        
        return correlation;
      }
      
      // Otherwise, try to get real correlation
      setApiCallCount(prev => prev + 1);
      const correlation = await alphaVantageService.getCorrelation(symbolAUpper, symbolBUpper);
      
      // Update the cache
      setMarketData(prev => {
        const updated = { ...prev };
        
        if (!updated[symbolAUpper]) {
          updated[symbolAUpper] = { lastPrice: 0, zscore: 0, volatility: 0, correlations: {} };
        }
        if (!updated[symbolBUpper]) {
          updated[symbolBUpper] = { lastPrice: 0, zscore: 0, volatility: 0, correlations: {} };
        }
        
        updated[symbolAUpper].correlations[symbolBUpper] = correlation;
        updated[symbolBUpper].correlations[symbolAUpper] = correlation;
        
        return updated;
      });
      
      toast({
        title: "Correlation Calculated",
        description: `Correlation between ${symbolAUpper} and ${symbolBUpper}: ${correlation.toFixed(2)}`,
        variant: "info",
      });
      
      return correlation;
    } catch (err) {
      console.error(`Error calculating correlation between ${symbolAUpper} and ${symbolBUpper}:`, err);
      // Return a random reasonable correlation on error
      const correlation = generateMockCorrelation();
      
      setMarketData(prev => {
        const updated = { ...prev };
        
        if (!updated[symbolAUpper]) {
          updated[symbolAUpper] = { lastPrice: 0, zscore: 0, volatility: 0, correlations: {} };
        }
        if (!updated[symbolBUpper]) {
          updated[symbolBUpper] = { lastPrice: 0, zscore: 0, volatility: 0, correlations: {} };
        }
        
        updated[symbolAUpper].correlations[symbolBUpper] = correlation;
        updated[symbolBUpper].correlations[symbolAUpper] = correlation;
        
        return updated;
      });
      
      toast({
        title: "Correlation Error",
        description: `Using estimated correlation for ${symbolAUpper} and ${symbolBUpper}`,
        variant: "warning",
      });
      
      return correlation;
    }
  }, [marketData, apiCallCount, isDevMode, toast, setMarketData, setApiCallCount]);

  return {
    getCorrelation
  };
};
